import React, { Component } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import Network from 'services/Network';
import { AppContext } from 'context/app-context';
import i18n from 'i18n';
import EventEmitter from 'utils/EventEmitter';
import NavigationBar from 'SharedComponents/NavigationBar';
import LoadingIndicator from 'SharedComponents/LoadingIndicator';
import { EventType } from 'Constants';
import C2LDownModal from 'SharedComponents/C2LDownModal';
import ConnectionDownModal from 'direct/Global/modals/ConnectionDownModal';
import BackToHomeModal from 'direct/Global/modals/BackToHomeModal';
import BackToEPFModal from 'components/Disability/Global/modals/BackToEPFModal';
import TagManager from 'react-gtm-module';
import Config from 'Config';
import { triggerGaPageview } from 'utils/ga';
import loadable from '@loadable/component';
import PolicyPayloadProvider from 'context/PolicyPayloadProvider';
import './styles.css';

// import images for og:image meta tag
// Note: We must import the images
// in order to tell webpack to include them in
// the bundle
import 'images/protect_og.jpg';
import 'images/cancer/care_og.jpg';
import 'images/medSecure/medSecure_og.jpg';
import 'images/fwd_logo_orange_bg.jpg';
import 'images/big3Product/big3Product_og.jpg';

// import Product landing pages here
const TermLife = loadable(() => import('src/components/TermLife'));
const TermCancer = loadable(() => import('src/components/TermCancer'));
const MedSecure = loadable(() => import('src/components/MedSecure'));
const B40 = loadable(() => import('src/components/B40'));
const Disability = loadable(() => import('src/components/Disability'));
// const SpecialMed = loadable(() => import('src/components/SpecialMed'));
const MedicalProduct = loadable(() => import('src/components/MedicalProduct'));
const Big3Product = loadable(() => import('src/components/Big3Product'));
const Wheel = loadable(() => import('src/components/Wheel'));
const Klook = loadable(() => import('src/components/Klook'));
const Telemarketing = loadable(() => import('src/components/Telemarketing'));
const EPF = loadable(() => import('src/components/EPF'));
const EPP = loadable(() => import('src/components/EPP'));
const EPPHome = loadable(() => import('src/components/EPP/Home'));
const ErrorCode = loadable(() => import('src/components/ErrorCode'));
const MyStorage = loadable(() => import('src/components/ErrorCode/MyStorage'));

// add google tag manager if the gtm config is enabled
const gtm = Config.gtm || {};
if (gtm.enable) {
  const tagManagerArgs = {
    gtmId: gtm.id
  };
  TagManager.initialize(tagManagerArgs);
}

// const ga = Config.ga || {};
// if (ga.enable) {
//   ReactGA.initialize(ga.id, ga.options);
//   ReactGA.pageview(window.location.pathname + window.location.search);
// }

const restrictedUrls = [
  '/contactMeDeclined',
  '/uploadNRIC',
  '/upload-documents', // Used in Disability
  '/underwriting/result',
  '/checkout',
  '/confirmation',
  '/confirm',
  '/technicaldifficulties',
  '/nomination',
  '/nomination/witness',
  '/nomination/OTP',
  '/nomination/OTP_Failed',
  '/nomination/technical_difficulties',
  '/nomination/success',
  '/paymentFaileContactMeDeclined'
];

const whitelistUrls = ['/nomination/executor', '/nomination/beneficiary'];

export default class App extends Component {
  static propTypes = {
    history: PropTypes.shape({
      listen: PropTypes.func,
      push: PropTypes.func,
      go: PropTypes.func,
      replace: PropTypes.func
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string
    })
  };

  constructor(props) {
    super(props);
    this.state = {
      logoLink: '/direct/protect-direct',
      setLogoLink: this.setLogoLink,
      getLogoLink: this.getLogoLink,
      showBackButton: true,
      setShowBackButton: this.setShowBackButton,
      setShowLanguageSwitch: this.setShowLanguageSwitch,
      changeLanguage: this.changeLanguage,
      setNavBarSecondLogo: this.setNavBarSecondLogo,
      backPath: '',
      isLoading: false,
      isErrorOccurred: false,
      currentErrorCode: '',
      isConnectionDown: false,
      isC2LDown: false,
      isBackToHomeModalShown: false,
      lng: 'en',
      showCancelButton: false,
      setShowCancelButton: this.setShowCancelButton,
      enableMedicalCarePlus: (Config.medicalCarePlus || {}).enabled,
      enableEpp: (Config.epp || {}).enabled,
      enableErrorPage: (Config.errorPage || {}).enabled,
      enableMyStoragePage: (Config.myStoragePage || {}).enabled
    };

    // Initialize singleton Network
    this.network = new Network();
    this.registerEvents();

    this.currentPathname = null;
    this.currentSearch = null;
  }

  componentDidMount() {
    const { history } = this.props;
    history.listen((newLocation, action) => {
      if (
        newLocation.pathname !== this.currentPathname ||
        newLocation.search !== this.currentSearch
      ) {
        // Prevent user right-click and choose a history in history stack
        const isStandOnRestrictedUrl =
          this.currentPathname &&
          restrictedUrls.find((url) => {
            return this.currentPathname.includes(url);
          });
        if (isStandOnRestrictedUrl && action === 'POP') {
          this.setState({ isBackToHomeModalShown: true });
          history.go(1);
          return;
        }

        // Trigger GA pageview
        triggerGaPageview();

        // Save new location
        this.currentPathname = newLocation.pathname;
        this.currentSearch = newLocation.search;

        // Verify whitelist or restricted urls
        const isWhitelistedUrls = whitelistUrls.find((url) => {
          return this.currentPathname.includes(url);
        });
        const isRestrictedUrls = restrictedUrls.find((url) => {
          return this.currentPathname.includes(url);
        });

        if (isWhitelistedUrls || !isRestrictedUrls) {
          return;
        }

        // Clone location object and push it to history
        // In case to prevent navigate back later
        history.push({
          pathname: newLocation.pathname,
          search: newLocation.search
        });
      } else {
        // Verify whitelist or restricted urls
        const isWhitelistedUrls = whitelistUrls.find((url) => {
          return this.currentPathname.includes(url);
        });
        const isRestrictedUrls = restrictedUrls.find((url) => {
          return this.currentPathname.includes(url);
        });

        if (isWhitelistedUrls || !isRestrictedUrls) {
          return;
        }

        if (action === 'POP') {
          this.setState({ isBackToHomeModalShown: true });
          history.go(1);
        }
      }
    });
  }

  registerEvents = () => {
    const ee = new EventEmitter();
    ee.removeAllListeners(); // Remove all listeners to prevent duplicated listeners
    ee.on(EventType.loading, (isLoading) => this.handleLoadingEvent(isLoading));
    ee.on(EventType.responseError, (errMsg) => this.handleGenericErrorEvent(errMsg));
    ee.on(EventType.connectionDown, (errMsg) => this.handleConnectionDownEvent(errMsg));
    ee.on(EventType.C2LDown, () => {
      this.handleShowModalC2LDown();
    });
    this.eventEmitter = ee;
  };

  handleLoadingEvent = (isLoading) => {
    this.setState({ isLoading });
    // lock scrollbar
    document.body.style.overflow = isLoading ? 'hidden' : '';
    // handle Safari ignores overflow style
    document.body.style.position = isLoading ? 'relative' : '';
  };

  handleGenericErrorEvent = (error) => {
    const { logoLink } = this.state;
    let paramErrorCode = '';
    if (error.errorCode) {
      paramErrorCode = `?error=${error.errorCode}`;
    }

    // Only redirect to technical difficulties page for e-commerce products
    if (
      logoLink.startsWith('/direct') &&
      /* temporary */ !window.location.pathname.includes('/medical-care/upload-documents')
    ) {
      this.props.history.push(`${logoLink}/technicaldifficulties${paramErrorCode}`);
    }
  };

  handleShowModalC2LDown = () => {
    this.setState({ isC2LDown: true });
  };

  handleHideModalC2LDown = () => {
    this.setState({ isC2LDown: false });
  };

  handleConnectionDownEvent = () => {
    this.setState({ isConnectionDown: true });
  };

  handleConnectionDownOnHide = () => {
    this.setState({ isConnectionDown: false });
  };

  handleBackToHomeModalHide = () => {
    this.setState({ isBackToHomeModalShown: false });
  };

  handleBackToHome = () => {
    const { history } = this.props;
    this.setState({ isBackToHomeModalShown: false });
    history.replace(this.state.logoLink);
  };

  redirectToContactMe = () => {
    // this.setState({ isErrorOccurred: false });

    const { logoLink } = this.state;
    // Only redirect to technical difficulties page for e-commerce products
    if (logoLink.startsWith('/direct')) {
      return (
        <Redirect
          to={{
            pathname: `${logoLink}/technicaldifficulties`,
            state: { basePath: logoLink }
          }}
        />
      );
    }
  };

  setLogoLink = (link) => {
    this.setState({
      logoLink: link || '/direct/protect-direct'
    });
  };

  getLogoLink = () => {
    return this.state.logoLink;
  };

  setShowBackButton = (backPath) => {
    this.setState({
      showBackButton: !!backPath,
      backPath: backPath ? backPath : ''
    });
  };

  setShowCancelButton = (cancelPath) => {
    this.setState({
      showCancelButton: !!cancelPath,
      cancelPath: cancelPath ? cancelPath : ''
    });
  };

  setShowLanguageSwitch = (showLanguageSwitch) => {
    this.setState({ showLanguageSwitch });
  };

  changeLanguage = (lng) => {
    this.setState({ lng });
    i18n.changeLanguage(lng);
  };

  setNavBarSecondLogo = (logo) => {
    this.setState({
      secondLogo: logo
    });
  };

  render() {
    const connectionDownProps = {
      isShowing: this.state.isConnectionDown,
      onHide: this.handleConnectionDownOnHide
    };

    const c2lDownModalProps = {
      isShowing: this.state.isC2LDown,
      onHide: this.handleHideModalC2LDown
    };

    const backToHomeProps = {
      isShowing: this.state.isBackToHomeModalShown,
      onBack: this.handleBackToHome,
      onHide: this.handleBackToHomeModalHide
    };

    const navProps = {
      logoLink: this.state.logoLink,
      canBack: this.state.showBackButton,
      backPath: this.state.backPath,
      secondLogo: this.state.secondLogo,
      showLanguageSwitch: this.state.showLanguageSwitch,
      canCancel: this.state.showCancelButton
    };

    // const epfOldProducts = ['ci', 'protect'];
    const epfNewProducts = ['epp', 'epl', 'testepf', 'testepfpay'];
    const epfProductPath = location.pathname.split('/').slice(1)[2];
    // const isEpfOldProduct = epfOldProducts.includes(epfProductPath);
    const isEpfNewProduct = epfNewProducts.includes(epfProductPath);

    let showNavigationBar = true;
    // const pathsIncludeHeaderMui = ['/direct/medical-care', '/direct/i-lindung-epf', '/direct/error-code'];
    // pathsIncludeHeaderMui.map(item => {
    //   if (
    //     this.props.location.pathname
    //       .toLowerCase()
    //       .includes(item.toLowerCase()) ||
    //       isEpfNewProduct
    //   ) {
    //     showNavigationBar = false;
    //   }
    // });
    const productPath = location.pathname.split('/').slice(1)[1];
    const isEpfHomePage = productPath === 'epf' && !epfProductPath;
    const productIncludeHeaderMui = ['medical-care', 'i-lindung-epf', 'error-code'];
    productIncludeHeaderMui.map((item) => {
      if (item === productPath || isEpfNewProduct || isEpfHomePage) {
        showNavigationBar = false;
      }
    });

    return (
      <AppContext.Provider value={this.state}>
        <PolicyPayloadProvider>
          <div className="App">
            {showNavigationBar && <NavigationBar {...navProps} />}
            <Switch>
              <Route exact path="/" render={() => <Redirect to="/direct/special-care" />} />
              <Route path="/direct/care-direct" component={TermCancer} />
              <Route path="/direct/medsecure-direct" component={MedSecure} />
              {/*  <Route path="/direct/kasih/cuepacs" component={B40} /> */}
              <Route path="/direct/kasih/sedania" component={B40} />
              {/*   <Route path="/direct/kasih/senang/petronas" component={B40} /> */}
              <Route path="/direct/kasih/ptv" component={B40} />
              <Route path="/direct/kasih" component={B40} />
              <Route path="/direct/special-care" component={Disability} />
              <Route
                path="/direct/SpecialMed"
                render={() => {
                  window.location.replace(
                    'https://www.fwd.com.my/medical-insurance-takaful/specialmed/'
                  );
                  return null;
                }}
              />
              <Route path="/direct/protect-direct" component={TermLife} />
              {this.state.enableMedicalCarePlus && (
                <Route path="/direct/medical-care" component={MedicalProduct} />
              )}

              <Route path="/direct/big-3-critical-illness" component={Big3Product} />
              <Route path="/direct/spin-the-wheel" component={Wheel} />
              <Route path="/direct/klook" component={Klook} />
              <Route path="/direct/telemarketing" component={Telemarketing} />
              {/* The shorten url is designed for reduce character used in sms */}
              <Route path="/direct/c" component={Telemarketing} />
              {this.state.enableEpp ? (
                <Route path="/direct/epf" component={!isEpfNewProduct ? EPF : EPP} />
              ) : (
                <Route path="/direct/epf" component={EPF} />
              )}
              <Route path="/direct/i-lindung-epf" component={EPPHome} />
              {this.state.enableErrorPage && (
                <Route path="/direct/error-code" component={ErrorCode} />
              )}
              {this.state.enableMyStoragePage && (
                <Route path="/direct/my-storage" component={MyStorage} />
              )}

              <Route render={() => <Redirect to="/direct/protect-direct" />} />
            </Switch>
            <LoadingIndicator isActive={this.state.isLoading} />
            {/* {this.state.isErrorOccurred && this.redirectToContactMe()}*/}
            <ConnectionDownModal {...connectionDownProps} />
            <C2LDownModal {...c2lDownModalProps} />
            {!EPF ? (
              <BackToHomeModal {...backToHomeProps} />
            ) : (
              <BackToEPFModal {...backToHomeProps} />
            )}
          </div>
        </PolicyPayloadProvider>
      </AppContext.Provider>
    );
  }
}
